import { Component, EventEmitter, Input, Optional, Output } from '@angular/core';
import { TranslateService }                                 from '@ngx-translate/core';

import { ChartCsvNamer }     from '../chart-csv-namer';
import { ChartPointModel }   from '../typings/chart-point';
import { ChartSeriesModel }  from '../typings/chart-series';
import { CSVService }        from '../../../../core/services/csv.service';
import { DonutSegmentModel } from '../typings/donut-segment';

// Card for a chart or table: title, description, clear chip, CSV export, loading and empty states
@Component({
  selector   : 'wor-chart-card',
  styles: [ require('./chart-card.component.scss') ],
  template: require('./chart-card.component.html')
})
export class ChartCardComponent {
  @Input() clearLabel     : string                   = '';
  @Input() csvLabelHeader : string                   = this.translateService.instant('GENERAL_SPACE.FIELD.DATE');
  @Input() csvName        : string;
  @Input() csvPoints      : Array<ChartPointModel>;
  @Input() csvRows        : Array<Record<string, unknown>>;
  @Input() csvSegments    : Array<DonutSegmentModel>;
  @Input() csvSeries      : Array<ChartSeriesModel>;
  @Input() description    : string                   = '';
  @Input() emptyHint      : string                   = '';
  @Input() emptyTitle     : string                   = '';
  @Input() isEmpty        : boolean                  = false;
  @Input() isLoading      : boolean                  = false;
  @Input() title          : string                   = '';

  @Output() cleared : EventEmitter<void> = new EventEmitter<void>();

  // when bound, the owner handles the export instead of the card building a CSV from its rows
  @Output() csvPressed : EventEmitter<void> = new EventEmitter<void>();

  // a page may provide ChartCsvNamer on its root component to name the files its cards download
  constructor (
    @Optional() private chartCsvNamer: ChartCsvNamer,
    private csvService               : CSVService,
    private translateService         : TranslateService
  ) {}

  clear () : void {
    this.cleared.emit();
  }

  download () : void {
    if ( this.csvPressed.observed ) {
      this.csvPressed.emit();

      return;
    }

    const name = this.csvName || this.title;

    this.csvService.download(
      this.chartCsvNamer
        ? this.chartCsvNamer.csvFileName(name)
        : this.csvService.fileName(name),
      this.buildRows()
    );
  }

  hasCsv () : boolean {
    return !!( ( this.csvRows && this.csvRows.length )
      || ( this.csvSegments && this.csvSegments.length )
      || ( this.csvPoints && this.csvPoints.length && this.csvSeries && this.csvSeries.length ) );
  }

  private buildRows () : Array<Record<string, unknown>> {
    if ( this.csvRows && this.csvRows.length ) {
      return this.csvRows;
    }

    if ( this.csvSegments && this.csvSegments.length ) {
      return this.csvSegments.map(segment => ({ label: segment.label, value: segment.value }));
    }

    return ( this.csvPoints || [] ).map(point => {
      const row : Record<string, unknown> = { [ this.csvLabelHeader ]: point.label };

      ( this.csvSeries || [] ).forEach(series => row[ series.label ] = point.values[ series.key ]);

      return row;
    });
  }
}
